import {
  Body1,
  Body2,
  Button,
  Card,
  Container,
  Display1,
  Display3,
  Flex,
  Grid,
  GridItem,
  Label1,
  Link,
} from '@sainsburys-tech/fable'
import { Close } from '@sainsburys-tech/icons'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { getQpigData } from '../../api/qpigApi'
import { getQpigImages } from '../../api/qpigImagesApi'
import { useApiClient } from '../../AppContext/appContext'
import useErrorHandler from '../../hooks/useErrorHandler'
import { IQpigImages } from '../../types/IQpigImages'
import { IStoreProduct } from '../../types/IStoreProduct'
import ProgressLoading from '../ProgressLoading'
import QpigIngredients from './QpigIngredients'
import QpigProcessSteps from './QpigProcessSteps'
import QpigShelfLife from './QpigShelfLife'

const DEFAULT_QPIG_IMAGES: IQpigImages = {
  baseFilePath: '',
  imageFileNames: [],
  signature: '?',
  lastModified: '',
}

const DEFAULT_STORE_PRODUCT: IStoreProduct = {
  sku: 0,
  description: '',
  qpigs: [],
  shelfLifeComments: [],
}

const useQpigData = (skuId: number) => {
  const apiClient = useApiClient()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [qpigData, setQpigData] = useState<IStoreProduct | null>(null)
  const [qpigImages, setQpigImages] = useState<IQpigImages>(DEFAULT_QPIG_IMAGES)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  useEffect(() => {
    const loadQpigData = async () => {
      try {
        setLoading(true)
        resetError()

        const [qpigDataResult, qpigImagesResult] = await Promise.allSettled([
          getQpigData(apiClient, skuId),
          getQpigImages(apiClient, skuId),
        ])

        // Handle QPIG data result
        if (qpigDataResult.status === 'fulfilled') {
          setQpigData(qpigDataResult.value)
        } else {
          console.warn('Failed to load QPIG data:', qpigDataResult.reason)
          setQpigData(DEFAULT_STORE_PRODUCT)
        }

        // Handle QPIG images result
        if (qpigImagesResult.status === 'fulfilled') {
          setQpigImages(qpigImagesResult.value)
        } else {
          console.warn('Failed to load QPIG images:', qpigImagesResult.reason)
          setQpigImages(DEFAULT_QPIG_IMAGES)
        }
      } catch (e) {
        console.error('Error loading QPIG data:', e)
        handleError('Failed to load QPIG data')
      } finally {
        setLoading(false)
      }
    }

    loadQpigData()
  }, [apiClient, skuId, handleError, resetError])

  return { loading, qpigData, qpigImages, errorMessage }
}

const QpigHeader: React.FC<{
  description: string
  sku: number
  onClose: () => void
}> = ({ description, sku, onClose }) => (
  <Grid>
    <GridItem colSpan={10}>
      <div className='c-qpig-table-margin'>
        <Display3 style={{ marginTop: '2rem', marginLeft: '2rem' }}>
          {description.replace('(Cold)', '').replace('Cold', '')} ({sku})
        </Display3>
      </div>
    </GridItem>
    <GridItem colSpan={2}>
      <div
        style={{ textAlign: 'right', marginTop: '2rem', marginRight: '2rem' }}
      >
        <Button
          onClick={onClose}
          testid='close-button-header'
          icon={<Close />}
          variant='tertiary'
          style={{
            position: 'absolute',
            top: '2rem',
            right: '2rem',
            zIndex: 2,
          }}
        />
      </div>
    </GridItem>
  </Grid>
)

const QpigError: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <Flex
    alignItems='center'
    justifyContent='center'
    style={{ paddingTop: '120px' }}
  >
    <Grid cols={1}>
      <div data-testid='no-qpig-error-info' id='noQpigErrorInfo'>
        <Display1 style={{ textAlign: 'center' }}>
          The QPIG data was not found
        </Display1>
      </div>
      <div style={{ textAlign: 'center' }}>
        <Button onClick={onClose} testid='close-button-error'>
          Close QPIG
        </Button>
      </div>
    </Grid>
  </Flex>
)

const MultipleQpigs: React.FC<{
  qpigData: IStoreProduct
  onSelectQpig: (index: number) => void
  onClose: () => void
}> = ({ qpigData, onSelectQpig, onClose }) => (
  <Container className='c-common-main-view-content'>
    <QpigHeader
      description={qpigData.description}
      sku={qpigData.sku}
      onClose={onClose}
    />
    <Card orientation='vertical'>
      {qpigData.qpigs.map((qpig, qpigIndex) => (
        <Body1 key={qpigIndex}>
          <Link
            className='c-common-hover-over-cursor c-qpig-link-underline'
            onClick={() => onSelectQpig(qpigIndex)}
            testid={`multiple-qpig-link-${qpigIndex}`}
            id='multipleQpig'
          >
            <Label1>PLU Code(s):</Label1>{' '}
            <Body2>{qpig.pluCodes.join(', ')}</Body2>
          </Link>
        </Body1>
      ))}
    </Card>
  </Container>
)

const SingleQpig: React.FC<{
  qpigData: IStoreProduct
  qpigIndex: number
  qpigImages: IQpigImages
  onClose: () => void
}> = ({ qpigData, qpigIndex, qpigImages, onClose }) => {
  const hasQpigs = qpigData.qpigs && qpigData.qpigs.length > 0
  const currentQpig = hasQpigs ? qpigData.qpigs[qpigIndex] : null

  return (
    <div>
      <QpigHeader
        description={qpigData.description}
        sku={qpigData.sku}
        onClose={onClose}
      />

      {!hasQpigs ? (
        <div id='errorInfo' style={{ textAlign: 'center' }}>
          <Display1>No Qpig Information</Display1>
        </div>
      ) : (
        <>
          <div className='c-qpig-table-margin'>
            <QpigIngredients
              additionalInformation={currentQpig!.additionalInformation}
            />
          </div>

          <div data-testid='qpig-steps' id='qpigSteps'>
            <QpigProcessSteps
              additionalInformation={currentQpig!.additionalInformation}
              pluCodes={currentQpig!.pluCodes}
              qpigImages={qpigImages}
            />
          </div>
        </>
      )}

      <div style={{ textAlign: 'center' }}>
        <Body2>
          <QpigShelfLife rawShelfLifeComments={qpigData.shelfLifeComments} />
        </Body2>
      </div>

      <div>
        <Button onClick={onClose} testid='close-button-singleqpig'>
          Close QPIG
        </Button>
      </div>
    </div>
  )
}

const QpigView: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const skuId = useMemo(() => parseInt(id ?? '', 10), [id])
  const [selectedQpigIndex, setSelectedQpigIndex] = useState(-1)

  const { loading, qpigData, qpigImages, errorMessage } = useQpigData(skuId)

  const goBack = useCallback(() => {
    navigate(-1)
    window.history.scrollRestoration = 'auto'
  }, [navigate])

  const handleSelectQpig = useCallback((index: number) => {
    setSelectedQpigIndex(index)
  }, [])

  const renderContent = useCallback(() => {
    if (loading) {
      return <ProgressLoading message='Loading...' />
    }

    if (errorMessage || !qpigData) {
      return <QpigError onClose={goBack} />
    }

    // Show selected QPIG
    if (selectedQpigIndex !== -1) {
      return (
        <SingleQpig
          qpigData={qpigData}
          qpigIndex={selectedQpigIndex}
          qpigImages={qpigImages}
          onClose={goBack}
        />
      )
    }

    // Show single QPIG if only one exists
    if (!qpigData.qpigs || qpigData.qpigs.length <= 1) {
      return (
        <SingleQpig
          qpigData={qpigData}
          qpigIndex={0}
          qpigImages={qpigImages}
          onClose={goBack}
        />
      )
    }

    // Show multiple QPIGs selection
    return (
      <MultipleQpigs
        qpigData={qpigData}
        onSelectQpig={handleSelectQpig}
        onClose={goBack}
      />
    )
  }, [
    loading,
    errorMessage,
    qpigData,
    selectedQpigIndex,
    qpigImages,
    goBack,
    handleSelectQpig,
  ])

  return (
    <Container className='c-common-main-view-content'>
      {renderContent()}
    </Container>
  )
}

export default QpigView
